import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";

import AdminServerList from "../datatable/AdminServerList";
import FreightRowNotifyToggles from "./FreightRowNotifyToggles";
import FreightActiveUserDtActions from "./FreightActiveUserDtActions";
import FreightNewUserDtActions from "./FreightNewUserDtActions";
import FreightInactiveUserDtActions from "./FreightInactiveUserDtActions";
import {
  getFreightActiveUserList,
  getFreightInactiveUserIndex,
  getFreightNewUserIndex
} from "../../utils/api";
import { parseFreightIndexResponse } from "../../utils/freightIndexResponse";

/**
 * Single freight user list implementation for **active**, **new**, and **inactive** queues.
 * Pages pass `variant`, column defs, visibility, search, page size, and the relevant action callbacks.
 */
const FreightUsersServerList = forwardRef(function FreightUsersServerList(
  {
    variant,
    columnDefs,
    visibility,
    search = "",
    pageSize,
    onPageSizeChange,
    lengthMenu,
    onCountsChange,
    getNavigate,
    onDeactivateUser,
    onVerify,
    onDeactivate,
    onReactivateUser,
    onColumnReorder
  },
  ref
) {
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => ({ reload: () => innerRef.current?.reload() }), []);

  const visibleDefsRef = useRef(columnDefs.filter((d) => visibility[d.key] !== false));
  visibleDefsRef.current = columnDefs.filter((d) => visibility[d.key] !== false);

  const getNavigateRef = useRef(getNavigate);
  getNavigateRef.current = getNavigate;
  const onDeactivateUserRef = useRef(onDeactivateUser);
  onDeactivateUserRef.current = onDeactivateUser;
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;
  const onDeactivateRef = useRef(onDeactivate);
  onDeactivateRef.current = onDeactivate;
  const onReactivateRef = useRef(onReactivateUser);
  onReactivateRef.current = onReactivateUser;

  const fetchPage = useCallback(
    async ({ page, pageSize: ps, search: q, sortBy, sortDir }) => {
      const keys = visibleDefsRef.current.map((d) => d.key);
      const sort =
        sortBy && keys.includes(sortBy) ? { sort_by: sortBy, sort_dir: sortDir || "desc" } : {};
      const payload = {
        page_number: page,
        page_size: ps,
        search_text: q || undefined,
        ...sort
      };
      let res;
      if (variant === "active") res = await getFreightActiveUserList(payload);
      else if (variant === "new") res = await getFreightNewUserIndex(payload);
      else res = await getFreightInactiveUserIndex(payload);
      const parsed = parseFreightIndexResponse(res, ps);
      return {
        list: parsed.list,
        recordsTotal: parsed.recordsTotal,
        recordsFiltered: parsed.recordsFiltered
      };
    },
    [variant]
  );

  const renderActions = useCallback(
    (row) => {
      if (variant === "active") {
        return (
          <FreightActiveUserDtActions
            row={row}
            onDeactivate={(id) => onDeactivateUserRef.current?.(id)}
            getNavigate={() => getNavigateRef.current?.()}
          />
        );
      }
      if (variant === "new") {
        return (
          <FreightNewUserDtActions
            row={row}
            onVerify={(id, status) => onVerifyRef.current?.(id, status)}
            onDeactivate={(id) => onDeactivateRef.current?.(id)}
            getNavigate={() => getNavigateRef.current?.()}
          />
        );
      }
      return (
        <FreightInactiveUserDtActions
          row={row}
          onReactivate={(id) => onReactivateRef.current?.(id)}
          getNavigate={() => getNavigateRef.current?.()}
        />
      );
    },
    [variant]
  );

  const prependColumns = useMemo(
    () => [
      {
        id: "alerts",
        header: "Alerts",
        headerTitle:
          variant === "active"
            ? "Notification preferences (saved on server)"
            : "Alert toggles (saved in this browser)",
        tdClassName: "text-center",
        renderCell: (row) => (
          <FreightRowNotifyToggles row={row} syncWithServer={variant === "active"} />
        )
      }
    ],
    [variant]
  );

  const emptyText = useMemo(() => {
    if (variant === "active") return "No active freight users.";
    if (variant === "new") return "No new freight users in the queue.";
    return "No inactive freight users.";
  }, [variant]);

  const visibilityKey = useMemo(() => JSON.stringify(visibility), [visibility]);

  return (
    <AdminServerList
      ref={innerRef}
      key={visibilityKey}
      fetchPage={fetchPage}
      search={search}
      showSearchInput={false}
      showPageSizeInToolbar={false}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      lengthMenu={lengthMenu}
      columnDefs={columnDefs}
      visibility={visibility}
      renderActions={renderActions}
      prependColumns={prependColumns}
      onColumnReorder={onColumnReorder}
      onCountsChange={onCountsChange}
      defaultSortKey="code"
      defaultSortDir="desc"
      emptyText={emptyText}
    />
  );
});

export default FreightUsersServerList;
